<template>
  <v-dialog v-model="showMonthlyTimeReport" @click:outside="showMonthlyTimeReport = false">
    <v-overlay :value="loaderOverlay" :absolute="true">
      <v-row justify="center">
        <v-progress-circular indeterminate size="120"></v-progress-circular>
      </v-row>
    </v-overlay>

    <v-card id="timeReport" min-height="90vh">
      <v-card-title class="mb-0 pb-0">
        <v-col cols="4"></v-col>

        <v-col cols="4">
          <v-row justify="center">
            <div v-if="isTimespan" style="font-size: 28px; font-weight: bold">
              {{ `${$t("timeReport")} ${fromDateAsString} - ${toDateAsString}` }}
            </div>
            <div style="font-size: 28px; font-weight: bold" v-else class="ml-12">
              {{ $t("timeReport") + " " + monthAsString + " " + yearAsString }}
            </div>
          </v-row>
        </v-col>

        <v-col cols="3">
          <v-row>
            <v-col cols="4" v-if="users.length > 1" class="d-flex justify-end"> </v-col>

            <v-spacer v-if="users.length == 1"></v-spacer>

            <v-col cols="8" class="d-flex justify-end">
              <v-select :items="exportType" v-model="selectedExportType" :label="$t('exportType')" hide-details class="pr-2"></v-select>
              <v-btn color="primary" class="mr-2" @click="exportReports()">{{ $t("exportBtnAll") }}</v-btn>
              <v-btn color="primary" @click="exportReport()">{{ $t("exportBtn") }}</v-btn>
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="1">
          <v-row no-gutters
            ><v-col class="d-flex justify-end">
              <v-btn @click="back" icon>
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-col></v-row
          ></v-col
        >
        <v-row no-gutters class="mb-2">
          <v-btn v-if="!isTimespan" @click="changeMonth(-1)" class="mt-6" icon text :style="{ cursor: 'pointer' }">
            <v-icon large>mdi-arrow-left</v-icon>
          </v-btn>

          <v-spacer></v-spacer>

          <v-col cols="3">
            <v-select v-model="selectedUser" item-text="name" :items="users" return-object :readonly="simplyReport" hide-details class="select"></v-select>
          </v-col>

          <v-spacer></v-spacer>

          <v-btn v-if="!isTimespan" @click="changeMonth(1)" class="mt-6" icon text :style="{ cursor: 'pointer' }">
            <v-icon large>mdi-arrow-right</v-icon>
          </v-btn>
        </v-row>
      </v-card-title>

      <v-card-text>
        <v-data-table
          :headers="headers"
          :items="selectedEmployee"
          item-key="date"
          dense
          :items-per-page="-1"
          fixed-header
          hide-default-footer
          class="elevation-1"
          :item-class="getRowClass"
        >
          <template #item.calenderWeek="{ item }">
            <v-row justify="center" class="pa-2" v-if="item.calenderWeek">
              <td class="bold" v-if="Number.isInteger(item.calenderWeek)">
                {{ $t("calenderWeek") + item.calenderWeek + ":" }}
              </td>
              <td v-else>
                {{ item.calenderWeek }}
              </td>
            </v-row>
          </template>
          <template #item.date="{ item }">
            <v-row>
              <td class="bold" v-if="item.date === 'overallPeriod'">
                {{ $t(item.date) }}
              </td>
              <td v-else>
                {{ item.date }}
              </td>
            </v-row>
          </template>
          <template #item.absence="{ item }">
            <div v-if="item.absence != null && item.absence.factor == 1" :style="setStyleForAbsence(item.absence)">{{ item.absence.reason.charAt(0) }}</div>
            <div v-else-if="item.absence != null && item.absence.factor != 1" :style="setStyleForAbsence(item.absence)">
              {{ item.absence.reason.charAt(0) + "/2" }}
            </div>
          </template>
        </v-data-table>

        <v-row no-gutters>
          <v-col cols="3">
            <v-simple-table class="mt-6">
              <thead>
                <tr>
                  <th></th>
                  <th>{{ $t("descriptions") }}</th>
                  <th>{{ $t("timeValue") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, i) in accountSummary" :key="i">
                  <td style="text-align: right">{{ row.operators }}</td>
                  <td>{{ $t(row.text) }}</td>
                  <td>{{ row.value }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-col>
          <v-col cols="3" v-if="selectedEmployee.length != 0" class="ml-6">
            <v-simple-table class="mt-6">
              <thead>
                <tr>
                  <th>{{ $t("descriptions") }}</th>
                  <th>{{ $t("timeValue") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{{ $t("totalVacation") }}</td>
                  <td>{{ vacationSummary.totalDays }}</td>
                </tr>
                <tr>
                  <td>{{ isTimespan ? $t("vacationTakenBeforeDate").format(fromDateAsString) : $t("vacationTakenPrevMonth") }}</td>
                  <td>{{ vacationSummary.usedPreMonths }}</td>
                </tr>
                <tr>
                  <td>{{ isTimespan ? $t("vacationTakenInPeriod") : $t("vacationTakenThisMonth") }}</td>
                  <td>{{ vacationSummary.usedThisMonth }}</td>
                </tr>
                <tr>
                  <td>{{ $t("remainingVacation") }}</td>
                  <td>{{ vacationSummary.remainingDays }}</td>
                </tr>
                <tr>
                  <td></td>
                  <td></td>
                </tr>
                <tr v-for="absence in Object.keys(absencesSummary)" :key="absence">
                  <td>{{ absence + " " + $t("total") }}</td>
                  <td>{{ calculateAbsences(absencesSummary[absence]) }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-col>
          <v-spacer></v-spacer>
          <!--v-col cols="3">
            <div v-if="abbreviations.length != 0">
              <div class="font-weight-bold">{{ $t("legend") }}</div>
              <v-row v-for="abbreviation of abbreviations" :key="abbreviation" no-gutters>
                <v-col cols="5">{{ $t(abbreviation) + ": " }}</v-col>
                <v-col>{{ $t(abbreviation + "Explain") }}</v-col>
              </v-row>
            </div>
          </v-col-->
        </v-row>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export default {
  name: "MonthlyTimeReport",
  data: (vm) => ({
    loaderOverlay: false,
    showMonthlyTimeReport: true,
    selectedUser: null,
    headers: [],
    picker: new Date().toISOString().substr(0, 7),
    monthAsString: "",
    yearAsString: "",
    fromDateAsString: "",
    toDateAsString: "",
    history: null,
    users: null,
    sum: null,
    employees: [],
    selectedEmployee: [],
    abbreviations: [],
    accountSummary: [],
    absencesSummary: [],
    vacationSummary: [],
    exportType: ["PDF", "CSV"],
    selectedExportType: "PDF"
  }),
  props: {
    items: Array,
    date: String,
    simplyReport: Boolean,
    isTimespan: Boolean,
    toDate: String,
    fromDate: String
  },
  watch: {
    showMonthlyTimeReport: function () {
      if (!this.showMonthlyTimeReport) {
        this.$emit("close");
        this.$destroy();
      }
    },
    selectedUser: function (user) {
      if (this.employees.length != 0) {
        this.selectedEmployee = this.employees.find((item) => item.userId == user.SAPEmpID).data;
        this.accountSummary = this.employees.find((item) => item.userId == user.SAPEmpID).accountSummary;
        this.absencesSummary = this.employees.find((item) => item.userId == user.SAPEmpID).absencesSummary;
        this.vacationSummary = this.employees.find((item) => item.userId == user.SAPEmpID).vacationSummary;
      }
    },
    users: function (users) {
      if (this.selectedUser != undefined) {
        this.selectedUser = this.users.find((user) => user.sapEmpId == this.selectedUser.sapEmpId);
      }
    }
  },
  mounted() {},
  created: function () {
    this.users = this.items;
    this.selectedUser = this.users[0];

    if (this.isTimespan) {
      const startDate = this.fromDate;
      const endDate = this.toDate;

      this.fromDateAsString = this.$moment(startDate).locale(this.$vuetify.lang.current).format("DD. MMMM YYYY");
      this.toDateAsString = this.$moment(endDate).locale(this.$vuetify.lang.current).format("DD. MMMM YYYY");
    } else {
      this.picker = this.date;
      this.monthAsString = this.$moment(this.picker).locale(this.$vuetify.lang.current).format("MMMM");
      this.yearAsString = this.$moment(this.picker).locale(this.$vuetify.lang.current).format("YYYY");
    }
    this.changeMonth(0);
  },
  methods: {
    back: function () {
      this.showMonthlyTimeReport = false;
    },
    async exportReport() {
      const delimiter = ";";
      const headers = [];
      let employee = this.employees.find((item) => item.userId == this.selectedUser.SAPEmpID);
      Object.keys(this.selectedEmployee[1]).map((item) => {
        if (item != "rowColor") headers.push(this.$t(item));
      });

      this.selectedEmployee.map((item) => {
        if (item == null) item = null;
      });
      const stringifiedElements = this.selectedEmployee.map((element) => {
        return Object.entries(element)
          .filter(([key]) => key !== "rowColor")
          .map(([_, elem]) => {
            if (elem == null) elem = "";
            if (typeof elem === "object") elem = elem.reason;
            if (elem == "overallPeriod") elem = this.$t("overallPeriod");
            elem = elem.toString();

            return elem[0] === "-" || elem.split("").includes(":") ? ` ${elem}` : elem;
          })
          .join(delimiter);
      });

      let pdfElements = [...stringifiedElements];

      // blank line as separator
      stringifiedElements.push("");

      // AccountSummary-Header
      const summaryHeaders = ["", this.$t("descriptions"), this.$t("timeValue")];
      stringifiedElements.push(summaryHeaders.join(delimiter));

      // AccountSummary-Dates

      const summaryRows = employee.accountSummary.map((row) => [row.operators || "", this.$t(row.text), row.value].join(delimiter));
      stringifiedElements.push(...summaryRows);

      // blank line as separator
      stringifiedElements.push("");

      // VacationSummary-Header
      stringifiedElements.push(this.$t("vacationSummary"));

      // VacationSummary-Dates
      const vac = employee.vacationSummary;
      stringifiedElements.push(`${this.$t("totalVacation")}${delimiter}${vac.totalDays}`);
      stringifiedElements.push(`${this.$t("vacationTakenPrevMonth")}${delimiter}${vac.usedPreMonths}`);
      stringifiedElements.push(`${this.$t("vacationTakenThisMonth")}${delimiter}${vac.usedThisMonth}`);
      stringifiedElements.push(`${this.$t("remainingVacation")}${delimiter}${vac.remainingDays}`);

      // blank line as separator
      stringifiedElements.push("");

      // AbsencesSummary-Header
      stringifiedElements.push(this.$t("absencesSummary"));

      // AbsencesSummary-Dates
      Object.keys(employee.absencesSummary).forEach((reason) => {
        stringifiedElements.push(`${reason}${delimiter}${employee.absencesSummary[reason].length}`);
      });

      const CSVOutput = `${headers.join(delimiter)}\n${stringifiedElements.join("\n")}`;

      if (this.selectedExportType === "PDF") this.exportPdfFile(pdfElements, headers, vac, employee.accountSummary, this.selectedUser.name);
      if (this.selectedExportType === "CSV") {
        this.downloadCSVFile(CSVOutput, this.selectedUser.name);
      }
    },

    async exportReports() {
      for (let employee of this.employees) {
        const delimiter = ";";
        // Report Header
        const headers = [];
        Object.keys(employee.data[1]).map((item) => {
          if (item != "rowColor") headers.push(this.$t(item));
        });

        // Report-Dates
        const stringifiedElements = employee.data.map((element) => {
          return Object.entries(element)
            .filter(([key]) => key !== "rowColor")
            .map(([_, elem]) => {
              if (elem == null) elem = "";
              if (typeof elem === "object") elem = elem.reason;
              if (elem == "overallPeriod") elem = this.$t("overallPeriod");
              elem = elem.toString();
              return elem[0] === "-" || elem.split("").includes(":") ? ` ${elem}` : elem;
            })
            .join(delimiter);
        });

        // blank line as separator
        stringifiedElements.push("");

        let rows = [...stringifiedElements];

        // AccountSummary-Header
        const summaryHeaders = ["", this.$t("descriptions"), this.$t("timeValue")];
        stringifiedElements.push(summaryHeaders.join(delimiter));

        // AccountSummary-Dates
        const summaryRows = employee.accountSummary.map((row) => [row.operators || "", this.$t(row.text), row.value].join(delimiter));
        stringifiedElements.push(...summaryRows);

        // blank line as separator
        stringifiedElements.push("");

        // VacationSummary-Header
        stringifiedElements.push(this.$t("vacationSummary"));

        // VacationSummary-Dates
        const vac = employee.vacationSummary;
        stringifiedElements.push(`${this.$t("totalVacation")}${delimiter}${vac.totalDays}`);
        stringifiedElements.push(`${this.$t("vacationTakenPrevMonth")}${delimiter}${vac.usedPreMonths}`);
        stringifiedElements.push(`${this.$t("vacationTakenThisMonth")}${delimiter}${vac.usedThisMonth}`);
        stringifiedElements.push(`${this.$t("remainingVacation")}${delimiter}${vac.remainingDays}`);

        // blank line as separator
        stringifiedElements.push("");

        // AbsencesSummary-Header
        stringifiedElements.push(this.$t("absencesSummary"));

        // AbsencesSummary-Dates
        Object.keys(employee.absencesSummary).forEach((reason) => {
          stringifiedElements.push(`${reason}${delimiter}${employee.absencesSummary[reason].length}`);
        });

        //  CSV
        const CSVOutput = `${headers.join(delimiter)}\n${stringifiedElements.join("\n")}`;

        if (this.selectedExportType === "PDF") this.exportPdfFile(rows, headers, vac, employee.accountSummary, employee.name);
        if (this.selectedExportType === "CSV") this.downloadCSVFile(CSVOutput, employee.name);
      }
    },
    exportPdfFile(rows, headers, vacationSummary, accountSummary, userName) {
      const doc = new jsPDF("landscape", "mm", "a4");

      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();

      const leftMargin = 5;
      const rightMargin = 5;
      const topMargin = 5;
      const bottomMargin = 5;

      const gap = 6;
      const usableW = pageW - leftMargin - rightMargin;
      const colW = (usableW - gap) / 2;

      rows = rows.filter((l) => l && l.trim().length > 0).map((l) => l.split(";").map((c) => c.trim()));

      // 1) MAIN TABLE
      autoTable(doc, {
        head: [headers],
        body: rows,
        margin: { top: topMargin, left: leftMargin, right: rightMargin, bottom: bottomMargin },
        tableWidth: "wrap",
        styles: { fontSize: 7, cellPadding: { top: 1, right: 1.5, bottom: 1, left: 1.5 }, valign: "middle", overflow: "linebreak" },
        headStyles: { fontSize: 7, cellPadding: { top: 1.5, right: 1.5, bottom: 1.5, left: 1.5 }, fillColor: [240, 240, 240] },
        theme: "plain"
      });

      // 2) compute startY AFTER main table
      const afterMainY = (doc.lastAutoTable?.finalY ?? topMargin) + 6;

      // 3) estimate if the two-column summary fits; if not -> new page
      const summaryFontSize = 8;
      const approxLineH = summaryFontSize * 0.3528 * 1.35; // mm, rough
      const accountLines = (accountSummary?.length ?? 0) + 1; // + header
      const vacLines = 4 + 1; // + header
      const neededH = Math.max(accountLines, vacLines) * approxLineH + 6;

      let startY = afterMainY;
      if (startY + neededH > pageH - bottomMargin) {
        doc.addPage("a4", "landscape");
        startY = topMargin;
      }

      // 4) LEFT: Account Summary
      const accountHead = [["", this.$t("descriptions"), this.$t("timeValue")]];
      const accountBody = accountSummary.map((r) => [r.operators || "", this.$t(r.text), String(r.value)]);

      autoTable(doc, {
        head: accountHead,
        body: accountBody,
        startY,
        margin: { left: leftMargin, right: rightMargin },
        tableWidth: colW,
        theme: "plain",
        styles: { fontSize: 8, cellPadding: 1 },
        columnStyles: {
          0: { cellWidth: 6, halign: "center" },
          1: { cellWidth: colW - 6 - 22, halign: "left" },
          2: { cellWidth: 22, halign: "right" } // values right
        },
        didParseCell: (data) => {
          if (data.section === "head" && data.column.index === 2) {
            data.cell.styles.halign = "right"; // header aligns with values
          }
          if (data.section === "head" && data.column.index === 0) {
            data.cell.styles.halign = "center";
          }
        }
      });

      // 5) RIGHT: Vacation Summary
      const vacHead = [[this.$t("vacationSummary"), this.$t("timeValue")]];
      const vacBody = [
        [this.$t("totalVacation"), String(vacationSummary.totalDays)],
        [this.$t("vacationTakenPrevMonth"), String(vacationSummary.usedPreMonths)],
        [this.$t("vacationTakenThisMonth"), String(vacationSummary.usedThisMonth)],
        [this.$t("remainingVacation"), String(vacationSummary.remainingDays)]
      ];

      autoTable(doc, {
        head: vacHead,
        body: vacBody,
        startY,
        margin: { left: leftMargin + colW + gap, right: rightMargin },
        tableWidth: colW,
        theme: "plain",
        styles: { fontSize: 8, cellPadding: 1 },
        columnStyles: {
          0: { cellWidth: colW - 22, halign: "left" },
          1: { cellWidth: 22, halign: "right" } // values right
        },
        didParseCell: (data) => {
          if (data.section === "head" && data.column.index === 1) {
            data.cell.styles.halign = "right"; // header aligns with values
          }
        }
      });
      const fileName = `COBI.time_${userName}_User_Report_${this.monthAsString}-${this.yearAsString}.pdf`;
      doc.save(fileName);
    },
    downloadCSVFile(csvString, userName) {
      const fileName = `COBI.time_${userName}_User_Report_${this.monthAsString}-${this.yearAsString}.csv`;

      // BOM für UTF-8
      const BOM = "\uFEFF";
      const blob = new Blob([BOM + csvString], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(url);
    },
    async changeMonth(change) {
      this.picker = this.$moment(this.picker).add(change, "months").format("YYYY-MM-DD");
      this.monthAsString = this.$moment(this.picker).locale(this.$vuetify.lang.current).format("MMMM");
      this.yearAsString = this.$moment(this.picker).locale(this.$vuetify.lang.current).format("YYYY");
      this.headers = [];
      if (this.employees.length != 0) {
        this.accountSummary = this.employees.find((item) => item.userId == this.selectedUser.SAPEmpID).accountSummary;
        this.absencesSummary = this.employees.find((item) => item.userId == this.selectedUser.SAPEmpID).absencesSummary;
        this.vacationSummary = this.employees.find((item) => item.userId == this.selectedUser.SAPEmpID).vacationSummary;
      }

      this.loaderOverlay = true;
      await this.$store
        .dispatch(this.isTimespan ? "users/reports/getTimespanHistory" : "users/reports/getMonthlyReports", {
          employees: this.users,
          date: this.picker,
          toDate: this.toDate,
          fromDate: this.fromDate
        })
        .then((resp) => {
          if (resp.data.length == 0) return;
          let columns = Object.keys(resp.data[0].data[1]);
          columns = columns.filter((item) => item !== "rowColor");
          columns.map((item) => this.headers.push({ text: this.$t(item), align: this.setAlign(item), value: item, sortable: false }));
          this.abbreviations = columns;
          this.abbreviations.splice(2, 1);

          this.employees = resp.data;
          if (this.selectedEmployee.length == 0) {
            this.selectedEmployee = resp.data[0].data;
            this.accountSummary = resp.data[0].accountSummary;
            this.absencesSummary = resp.data[0].absencesSummary;
            this.vacationSummary = resp.data[0].vacationSummary;
          } else {
            this.selectedEmployee = resp.data.find((item) => item.userId == this.selectedUser.SAPEmpID).data;
            this.accountSummary = resp.data.find((item) => item.userId == this.selectedUser.SAPEmpID).accountSummary;
            this.absencesSummary = resp.data.find((item) => item.userId == this.selectedUser.SAPEmpID).absencesSummary;
            this.vacationSummary = resp.data.find((item) => item.userId == this.selectedUser.SAPEmpID).vacationSummary;
          }
        })
        .catch((err) => {
          console.log(err);
          this.$eventHub.$emit("showError", this.$t(err.response.data.error));
        });

      this.loaderOverlay = false;
    },
    calculateAbsences(absences) {
      let absencesForMonth = 0;
      absences.map((item) => (absencesForMonth = absencesForMonth + item.factor));
      return absencesForMonth.toFixed(1);
    },
    setAlign(item) {
      if (item == "dayBalance" || item == "timeBalance") {
        return "end";
      } else return "start";
    },
    getRowClass(item, index) {
      if (typeof index === "undefined") {
        index = this.selectedEmployee.indexOf(item);
      }
      if (item.rowColor === 1 && index % 2 === 0) {
        return "row-darkgreen";
      } else if (item.rowColor === 1 && index % 2 !== 0) {
        return "row-green";
      } else if (item.rowColor === 2 && index % 2 === 0) {
        return "row-darkyellow";
      } else {
        return "row-yellow";
      }
    },
    setStyleForAbsence(absence) {
      return `background-color: ${absence.colorCode}; width: 30px; text-align: center`;
    }
  }
};
</script>

<style>
.row-green {
  background-color: #d4f7d4 !important;
}
.row-darkgreen {
  background-color: #a8e6a8 !important;
}
.row-yellow {
  background-color: #fff9c4 !important;
}
.row-darkyellow {
  background-color: #fff176 !important;
}
.theme--dark .row-green {
  background-color: #616161 !important;
}
.theme--dark .row-darkgreen {
  background-color: #424242 !important;
}
.theme--dark .row-yellow {
  background-color: #455a64 !important;
}
.theme--dark .row-darkyellow {
  background-color: #37474f !important;
}
.simple-table-wrapper {
  margin: 16px 0;
  overflow-x: auto;
}

.simple-table {
  width: 100%;
  border-collapse: collapse;
}

.simple-table th,
.simple-table td {
  border: 1px solid #ddd;
  padding: 8px;
  text-align: left;
}

.simple-table th {
  background-color: #f2f2f2;
  font-weight: bold;
}

.v-data-table__wrapper {
  max-height: 55vh !important;
  overflow-y: auto !important;
}
</style>
